import React, { useState } from 'react';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const ImageUploader = ({ user }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadedUrls, setUploadedUrls] = useState([]);
    const [dragOver, setDragOver] = useState(false);

    const handleFileSelect = (file) => {
        if (file && file.type.startsWith('image/')) {
            setSelectedFile(file);
        } else {
            alert('Prosím vyberte pouze obrázek');
        }
    };

    const handleFileInputChange = (e) => {
        const file = e.target.files[0];
        handleFileSelect(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        handleFileSelect(file);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const uploadImage = async () => {
        if (!selectedFile) {
            alert('Vyberte obrázek');
            return;
        }

        setUploading(true);
        try {
            const storage = getStorage();
            const timestamp = Date.now();
            const fileName = `${timestamp}_${selectedFile.name}`;
            const storageRef = ref(storage, `blog-images/${fileName}`);
            
            console.log('Uploading image:', fileName);
            await uploadBytes(storageRef, selectedFile);
            const downloadURL = await getDownloadURL(storageRef);
            
            console.log('Image uploaded successfully:', downloadURL);
            
            // Add to uploaded URLs list
            setUploadedUrls(prev => [{
                name: selectedFile.name,
                url: downloadURL,
                timestamp: new Date().toLocaleString('cs-CZ')
            }, ...prev]);
            
            // Clear selected file
            setSelectedFile(null);
            
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Chyba při nahrávání obrázku: ' + error.message);
        } finally {
            setUploading(false);
        }
    };

    const copyToClipboard = (url) => {
        navigator.clipboard.writeText(url).then(() => {
            alert('URL zkopírována do schránky!');
        }).catch(() => {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = url;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            alert('URL zkopírována do schránky!');
        });
    };

    const clearHistory = () => {
        setUploadedUrls([]);
    };

    if (!user) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-400">Přihlaste se pro přístup k nahrávání obrázků</p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            <div className="mb-8">
                <h2 className="text-3xl font-bold mb-4">Nahrávání obrázků</h2>
                <p className="text-gray-400">Nahrajte obrázky a získejte jejich Firebase Storage URL pro použití v blozích.</p>
            </div>

            {/* Upload Area */}
            <div className="bg-gray-800 rounded-xl p-6 mb-8">
                <div 
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                        dragOver 
                            ? 'border-yellow-500 bg-yellow-500/10' 
                            : 'border-gray-600 hover:border-gray-500'
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                >
                    <div className="mb-4">
                        <i className="fas fa-cloud-upload-alt text-4xl text-gray-400"></i>
                    </div>
                    
                    {selectedFile ? (
                        <div className="mb-4">
                            <p className="text-white mb-2">Vybraný soubor:</p>
                            <p className="text-yellow-500 font-medium">{selectedFile.name}</p>
                            <p className="text-gray-400 text-sm">
                                Velikost: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                        </div>
                    ) : (
                        <div className="mb-4">
                            <p className="text-white mb-2">Přetáhněte obrázek sem nebo klikněte pro výběr</p>
                            <p className="text-gray-400 text-sm">Podporované formáty: JPG, PNG, GIF, WebP</p>
                        </div>
                    )}

                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileInputChange}
                        className="hidden"
                        id="file-input"
                    />
                    
                    <div className="space-y-3">
                        <label
                            htmlFor="file-input"
                            className="inline-block bg-gray-700 hover:bg-gray-600 text-white px-6 py-2 rounded-lg cursor-pointer transition-colors"
                        >
                            Vybrat soubor
                        </label>
                        
                        {selectedFile && (
                            <div>
                                <button
                                    onClick={uploadImage}
                                    disabled={uploading}
                                    className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-600 text-black font-bold py-2 px-6 rounded-lg transition-colors ml-3"
                                >
                                    {uploading ? 'Nahrávání...' : 'Nahrát obrázek'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Uploaded Images History */}
            {uploadedUrls.length > 0 && (
                <div className="bg-gray-800 rounded-xl p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold">Historie nahraných obrázků</h3>
                        <button
                            onClick={clearHistory}
                            className="text-red-400 hover:text-red-300 text-sm"
                        >
                            Vymazat historii
                        </button>
                    </div>
                    
                    <div className="space-y-4">
                        {uploadedUrls.map((item, index) => (
                            <div key={index} className="bg-gray-700 rounded-lg p-4">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <h4 className="font-medium text-white mb-1">{item.name}</h4>
                                        <p className="text-gray-400 text-sm mb-2">{item.timestamp}</p>
                                        <div className="bg-gray-800 rounded p-2 mb-3">
                                            <code className="text-green-400 text-sm break-all">{item.url}</code>
                                        </div>
                                        <button
                                            onClick={() => copyToClipboard(item.url)}
                                            className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1 rounded transition-colors"
                                        >
                                            <i className="fas fa-copy mr-1"></i>
                                            Kopírovat URL
                                        </button>
                                    </div>
                                    <div className="ml-4">
                                        <img 
                                            src={item.url} 
                                            alt={item.name}
                                            className="w-20 h-20 object-cover rounded-lg"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Instructions */}
            <div className="bg-gray-800 rounded-xl p-6 mt-8">
                <h3 className="text-xl font-bold mb-4">Jak použít URL v blozích:</h3>
                <div className="space-y-2 text-gray-300">
                    <p>1. Nahrajte obrázek pomocí tohoto nástroje</p>
                    <p>2. Zkopírujte získanou URL</p>
                    <p>3. Jděte do Firebase Console nebo použijte Firebase Admin SDK</p>
                    <p>4. Najděte příslušný blog post v kolekci <code className="bg-gray-700 px-2 py-1 rounded">blogPosts</code></p>
                    <p>5. Aktualizujte pole <code className="bg-gray-700 px-2 py-1 rounded">image</code> s novou URL</p>
                </div>
            </div>
        </div>
    );
};

export default ImageUploader;